import {
	LanguageService,
	normalizeTranslationKey,
} from '@supply-mover-2-0/core';
import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';

interface TranslationContextValue {
	translations: Record<string, string>;
	isLoading: boolean;
	error: string | null;
	currentLanguage: string | null;
	refreshTranslations: () => Promise<void>;
	/**
	 * Translate a key. Optional second arg is EITHER a fallback string
	 * (legacy 2-arg signature) OR a plain-object of interpolation params
	 * that fill `{{name}}` placeholders in the resolved template. Mixing
	 * the two forms in the same call is not supported.
	 *
	 * Examples:
	 *   t('Save')                                  // → "Save"
	 *   t('UnknownKey', 'Fallback')                // → "Fallback"
	 *   t('Hello {{name}}', { name: 'Chris' })     // → "Hello Chris"
	 *   t('{{count}} files', { count: 3 })         // → "3 files"
	 */
	t: (
		key: string,
		defaultValueOrParams?:
			| string
			| Record<string, string | number | null | undefined>,
	) => string;
	hasLoadedOnce: boolean;
	translationUpdateKey: number; // This will change when translations update
}

const TranslationContext = createContext<TranslationContextValue | undefined>(
	undefined,
);

export interface TranslationProviderProps {
	children: ReactNode;
	/**
	 * Custom function to fetch translations for a given language code.
	 * When provided, bypasses the default LanguageService.fetchTranslations.
	 * Used by the portal app to fetch translations through portal API endpoints.
	 */
	fetchTranslations?: (
		code: string,
		bustCache?: boolean,
	) => Promise<Record<string, string>>;
	/**
	 * Custom function to get the user's preferred language code.
	 * When provided, bypasses the default LanguageService.getPreferredLanguage.
	 * Used by the portal app to read preference from the portal auth store.
	 */
	fetchPreferredLanguage?: () => Promise<string>;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
	children,
	fetchTranslations: customFetchTranslations,
	fetchPreferredLanguage: customFetchPreferredLanguage,
}) => {
	const [translations, setTranslations] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState(false); // Not loading initially
	const [error, setError] = useState<string | null>(null);
	const [currentLanguage, setCurrentLanguage] = useState<string | null>(null);
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const [translationVersion, setTranslationVersion] = useState(0); // Force re-renders
	const languageServiceRef = useRef<typeof LanguageService.prototype | null>(
		null,
	);
	const loadingRef = useRef<boolean>(false);

	const loadTranslations = React.useCallback(
		async (forceRefresh = false) => {
			// Prevent multiple concurrent calls using ref
			if (loadingRef.current) {
				return;
			}

			if (hasLoadedOnce && !forceRefresh) {
				return;
			}

			loadingRef.current = true;
			try {
				setIsLoading(true);
				setError(null);

				// Resolve preferred language — use custom fetcher if provided,
				// otherwise fall back to the default LanguageService
				let language: string;

				if (customFetchPreferredLanguage != null) {
					language = await customFetchPreferredLanguage();
				} else {
					if (languageServiceRef.current == null) {
						languageServiceRef.current = LanguageService.getInstance();
					}
					language = await languageServiceRef.current.getPreferredLanguage();
				}

				setCurrentLanguage(language);

				// Fetch translations — use custom fetcher if provided
				try {
					let translationData: Record<string, string>;

					if (customFetchTranslations != null) {
						translationData = await customFetchTranslations(
							language,
							forceRefresh,
						);
					} else {
						if (languageServiceRef.current == null) {
							languageServiceRef.current = LanguageService.getInstance();
						}
						translationData =
							await languageServiceRef.current.fetchTranslations(
								language,
								forceRefresh,
							);
					}

					// Normalize keys the same way the lookup does, so a stored token
					// answers every spelling the database collation folds onto it
					const normalizedTranslations: Record<string, string> = {};
					Object.entries(translationData).forEach(([key, value]) => {
						normalizedTranslations[normalizeTranslationKey(key)] = value;
					});

					// Force a new object reference to ensure React detects the change
					setTranslations(() => ({ ...normalizedTranslations }));
					// Increment version to force re-renders of components using translations
					setTranslationVersion((v) => v + 1);
				} catch (_fetchError) {
					// If translation fetch fails (e.g., user not authenticated), use empty translations
					setTranslations({});
					setTranslationVersion((v) => v + 1);
				}

				setHasLoadedOnce(true);
			} catch (err) {
				// Error: Failed to load translations:
				setError(
					err instanceof Error ? err.message : 'Failed to load translations',
				);
				// Set empty translations object to prevent errors
				setTranslations({});
				setHasLoadedOnce(true); // Mark as loaded even on error
			} finally {
				setIsLoading(false);
				loadingRef.current = false;
			}
		},
		[hasLoadedOnce, customFetchTranslations, customFetchPreferredLanguage],
	);

	// Don't load translations on mount - wait for explicit call after login
	useEffect(() => {
		// Translations will be loaded after successful login
	}, []);

	// Translation function with fallback (keys normalized by
	// `normalizeTranslationKey`, matching the DB collation) + `{{name}}`
	// interpolation. The optional second argument is overloaded:
	//   - string → legacy fallback when the key isn't in the translation map
	//   - object → interpolation params; missing keys leave the `{{name}}`
	//              placeholder literal so it's visible (NEVER stringified to
	//              "[object Object]")
	// See type declaration above for examples.
	const t = React.useCallback(
		(
			key: string,
			defaultValueOrParams?:
				| string
				| Record<string, string | number | null | undefined>,
		): string => {
			// Defensive check for non-string keys
			if (typeof key !== 'string') {
				// Warning: Translation key must be a string, received:
				// If it's an object with a type property, handle it specially
				if (typeof key === 'object' && key != null && 'type' in key) {
					// Error: Object with type property passed to translation:
					return '[Translation Error]';
				}

				return String(key);
			}

			// Split the second arg into its two possible roles
			const params =
				defaultValueOrParams != null && typeof defaultValueOrParams === 'object'
					? defaultValueOrParams
					: null;
			const defaultValue =
				typeof defaultValueOrParams === 'string'
					? defaultValueOrParams
					: undefined;

			const normalizedKey = normalizeTranslationKey(key);
			const lookup = translations[normalizedKey] ?? defaultValue ?? key;

			// Defensive: if the lookup somehow returned a non-string, coerce
			// safely (this should never happen now that the function signature
			// excludes objects from the default-value position, but keep the
			// guard for legacy callers that bypass TypeScript).
			const template = typeof lookup === 'string' ? lookup : String(lookup);

			// Apply `{{name}}` interpolation. Missing keys leave the placeholder
			// in the output so the bug is visible during dev rather than
			// stringifying to "[object Object]".
			if (params == null) {
				return template;
			}
			return template.replace(/\{\{(\w+)\}\}/g, (placeholder, varName) => {
				const value = params[varName];
				if (value == null) return placeholder;
				return String(value);
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[translations, translationVersion],
	); // Re-create function when translations or version change

	const refreshTranslations = React.useCallback(
		() => loadTranslations(true),
		[loadTranslations],
	);

	const value: TranslationContextValue = React.useMemo(
		() => ({
			translations,
			isLoading,
			error,
			currentLanguage,
			refreshTranslations,
			t,
			hasLoadedOnce,
			translationUpdateKey: translationVersion,
		}),
		[
			translations,
			isLoading,
			error,
			currentLanguage,
			refreshTranslations,
			t,
			hasLoadedOnce,
			translationVersion,
		],
	);

	return (
		<TranslationContext.Provider value={value}>
			{children}
		</TranslationContext.Provider>
	);
};

export const useTranslation = (): TranslationContextValue => {
	const context = useContext(TranslationContext);
	const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);

	if (context === undefined) {
		throw new Error('useTranslation must be used within a TranslationProvider');
	}

	// Force re-render when translations change
	React.useEffect(() => {
		forceUpdate();
	}, [context.translationUpdateKey]);

	return context;
};
